/**
 * the reflection module for property defintions
 */
import _ from 'underscore';
import type { PropertyName } from '../types';

interface ClassLike<TInstance extends object> {
  readonly name?: string;
  readonly prototype: TInstance;
}

/**
* Define a const property
* @param {Object} obj - the host object of the proprty
* @param {String} name - the name of the property
* @param {Object} value - the value of the property
* @return {Object} the host object
*/
export function defineConstProperty<
  TObject extends object,
  TName extends PropertyName,
  TValue,
>(obj: TObject, name: TName, value: TValue): TObject & Record<TName, TValue> {
  return Object.defineProperty(obj, name, {
    value,
    enumerable: true,
    writable: false,
  }) as TObject & Record<TName, TValue>;
}

/**
* Define a produced property with a factory
* @param {Object} obj - the host object of the proprty
* @param {String} name - the name of the property
* @param {Function} factory - the factory to produce the property value
* @return {Object} the host object
*/
export function defineProducedProperty<
  TObject extends object,
  TName extends PropertyName,
  TValue,
>(
  obj: TObject,
  name: TName,
  factory: (this: TObject) => TValue,
): TObject & Record<TName, TValue> {
  return Object.defineProperty(obj, name, {
    get() {
      const value = factory.call(obj);

      defineConstProperty(obj, name, value);
      return value;
    },
    enumerable: true,
    configurable: true,
  }) as TObject & Record<TName, TValue>;
}

/**
* Define a produced property on a class
* @param {Class} Class - the host class of the proprty
* @param {String} name - the name of the property
* @param {Function} factory - the factory to produce the property value
* @return {Class} the host class
*/
export function defineProducedPropertyOnClass<
  TInstance extends object,
  TName extends PropertyName,
  TValue,
>(
  Class: ClassLike<TInstance>,
  name: TName,
  factory: (this: TInstance) => TValue,
) {
  const RAND_MAX = 65535;
  const className = Class.name || `Anony${_.random(0, RAND_MAX)}`;
  const symbol = `__${className}_${name}`;

  Object.defineProperty(Class.prototype, name, {
    get() {
      if (!Object.prototype.hasOwnProperty.call(this, symbol)) {
        defineConstProperty(this, symbol, factory.call(this));
      }
      return (this as Record<string, TValue>)[symbol];
    },
    enumerable: true,
  });

  return Class;
}

/**
* Define a computed property whose value is computed each time the getter being called
* @param {Object} obj - the host object of the proprty
* @param {String} name - the name of the property
* @param {Function} getter - the getter to compute the property value
* @return {Object} the host object
*/
export function defineComputedProperty<
  TObject extends object,
  TName extends PropertyName,
  TValue,
>(
  obj: TObject,
  name: TName,
  getter: (this: TObject) => TValue,
): TObject & Record<TName, TValue> {
  return Object.defineProperty(obj, name, {
    get: getter,
    enumerable: true,
  }) as TObject & Record<TName, TValue>;
}

// Make the functions chainable with underscorejs
_.mixin({
  defineConstProperty,
  defineProducedProperty,
  defineProducedPropertyOnClass,
  defineComputedProperty,
});

/**
* Detect whether or not a object has certain property without evaluation
* @param {Object} obj - the host class of the proprty
* @param {String} name - the name of the property
* @return {Boolean} whether the property is defined
*/
export function hasOwnProperty(obj: object, name: PropertyName) {
  return !!Object.getOwnPropertyDescriptor(obj, name);
}
